"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MultivariateClustering = void 0;
const three_1 = require("three");
const THREE = require("three");
const DisplayModeDuck_1 = require("../../Ducks/DisplayModeDuck");
const React = require("react");
const react_redux_1 = require("react-redux");
const UtilityFunctions_1 = require("../UtilityFunctions");
const NamedCategoricalScales_1 = require("../../util/Colors/NamedCategoricalScales");
const TrailVisualization_1 = require("./TrailVisualization");
const ClusterModeDuck_1 = require("../../Ducks/ClusterModeDuck");
const core_1 = require("@material-ui/core");
const ViewTransform_1 = require("../ViewTransform");
const SELECTED_COLOR = 0x4d94ff;
const DEFAULT_COLOR = 0x000000;
const GRAYED = 0x3c3c3c;
const mapState = (state) => ({
    dataset: state.dataset,
    clusters: state.currentClusters,
    displayMode: state.displayMode,
    webGLView: state.webGLView,
    selectedClusters: state.selectedClusters,
    clusterMode: state.clusterMode,
    trailSettings: state.trailSettings,
    stories: state.stories,
    globalPointSize: state.globalPointSize,
    viewTransform: state.viewTransform
});
const connector = react_redux_1.connect(mapState, null, null, { forwardRef: true });
/**
 * Clustering visualization as a React component.
 */
exports.MultivariateClustering = connector(class extends React.Component {
    constructor(props) {
        super(props);
        this.clusterObjects = [];
        this.devicePixelRatio = window.devicePixelRatio;
        this.scalingScene = new THREE.Scene();
        this.scene = new THREE.Scene();
        this.clusterScene = new THREE.Scene();
        let arrowMateral = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, vertexColors: true });
        arrowMateral.opacity = 0.8;
        arrowMateral.transparent = true;
        this.arrowMesh = new THREE.Mesh(new THREE.Geometry(), arrowMateral);
        this.scene.add(this.arrowMesh);
        this.trailMesh = new THREE.Mesh(new THREE.Geometry(), new THREE.MeshBasicMaterial({ color: 0xff0000, side: THREE.DoubleSide, vertexColors: true }));
        this.scene.add(this.trailMesh);
        this.trail = new TrailVisualization_1.TrailVisualization();
        this.trail.create(this.clusterObjects);
        this.scene.add(this.trail.mesh);
        this.triangulationWorker = new Worker('dist/tessy.js');
    }
    componentDidUpdate(prevProps) {
        if (prevProps.trailSettings != this.props.trailSettings) {
            this.trail.setVisible(this.props.trailSettings.show);
            this.trail.setLength(this.props.trailSettings.length);
        }
        // If we have clusters now... and are on clusters tab... create cluster visualization
        if (!UtilityFunctions_1.arraysEqual(prevProps.clusters, this.props.clusters)) {
            this.destroy();
            this.disposeTriangulatedMesh();
            if (this.props.clusters && this.props.clusters.length > 0) {
                if (this.props.dataset.multivariateLabels) {
                    this.create();
                }
                else {
                    this.create();
                    this.createTriangulatedMesh();
                }
            }
        }
        if (prevProps.displayMode != this.props.displayMode) {
            switch (this.props.displayMode) {
                case DisplayModeDuck_1.DisplayMode.StatesAndClusters:
                    this.highlightCluster(this.props.selectedClusters);
                    break;
                case DisplayModeDuck_1.DisplayMode.OnlyClusters:
                    this.clusterObjects.forEach(clusterObject => {
                        clusterObject.children.forEach(child => {
                            child.visible = false;
                        });
                    });
                    break;
            }
        }
    }
    updateArrows(zoom) {
        var _a;
        if (!this.arrowMesh) {
            return;
        }
        this.arrowMesh.visible = true;
        this.arrowMesh.geometry.dispose();
        let arrowGeometry = new THREE.Geometry();
        arrowGeometry.vertices = [];
        arrowGeometry.faces = [];
        let index = 0;
        (_a = this.props.stories.active) === null || _a === void 0 ? void 0 : _a.edges.forEach(edge => {
            let color = new THREE.Color(0x000000);
            if (this.props.stories.trace && this.props.stories.trace.mainEdges.includes(edge)) {
                color = new THREE.Color(SELECTED_COLOR);
            }
            let start = new THREE.Vector2(edge.source.getCenter().x, edge.source.getCenter().y);
            let end = new THREE.Vector2(edge.destination.getCenter().x, edge.destination.getCenter().y);
            let middle = new THREE.Vector2().addVectors(start, new THREE.Vector2().subVectors(end, start).multiplyScalar(0.5));
            let dir = end.clone().sub(start).normalize();
            let markerOffset = start.clone().sub(end).normalize().multiplyScalar(24);
            let left = new three_1.Vector2(-dir.y, dir.x).multiplyScalar(2);
            let right = new three_1.Vector2(dir.y, -dir.x).multiplyScalar(2);
            let offset = dir.clone().multiplyScalar(this.devicePixelRatio * 12);
            // line without arrow
            arrowGeometry.vertices.push(new THREE.Vector3(start.x * zoom + left.x + offset.x, start.y * zoom + left.y + offset.y, 0));
            arrowGeometry.vertices.push(new THREE.Vector3(start.x * zoom + right.x + offset.x, start.y * zoom + right.y + offset.y, 0));
            arrowGeometry.vertices.push(new THREE.Vector3(end.x * zoom + left.x - offset.x, end.y * zoom + left.y - offset.y, 0));
            arrowGeometry.vertices.push(new THREE.Vector3(end.x * zoom + right.x - offset.x, end.y * zoom + right.y - offset.y, 0));
            // left wing
            arrowGeometry.vertices.push(new THREE.Vector3(middle.x * zoom + left.x - offset.x, middle.y * zoom + left.y - offset.y, 0));
            arrowGeometry.vertices.push(new THREE.Vector3(middle.x * zoom + left.x + offset.x, middle.y * zoom + left.y + offset.y, 0));
            arrowGeometry.vertices.push(new THREE.Vector3(middle.x * zoom + left.x * 3 - offset.x, middle.y * zoom + left.y * 3 - offset.y, 0));
            // Right wing
            arrowGeometry.vertices.push(new THREE.Vector3(middle.x * zoom + right.x - offset.x, middle.y * zoom + right.y - offset.y, 0));
            arrowGeometry.vertices.push(new THREE.Vector3(middle.x * zoom + right.x + offset.x, middle.y * zoom + right.y + offset.y, 0));
            arrowGeometry.vertices.push(new THREE.Vector3(middle.x * zoom + right.x * 3 - offset.x, middle.y * zoom + right.y * 3 - offset.y, 0));
            let i = index * 10;
            // line without arrow
            arrowGeometry.faces.push(new THREE.Face3(i, i + 1, i + 2, new three_1.Vector3(0, 0, -1), color));
            arrowGeometry.faces.push(new THREE.Face3(i + 1, i + 3, i + 2, new three_1.Vector3(0, 0, -1), color));
            // left wing
            arrowGeometry.faces.push(new THREE.Face3(i + 4, i + 6, i + 5, new three_1.Vector3(0, 0, -1), color));
            // Right wing
            arrowGeometry.faces.push(new THREE.Face3(i + 7, i + 9, i + 8, new three_1.Vector3(0, 0, -1), color));
            index = index + 1;
        });
        this.arrowMesh.geometry = arrowGeometry;
    }
    /**
     * Updates geometry of the trail mesh.
     */
    updateTrail(zoom) {
        this.trail.update(this.clusterObjects, zoom);
    }
    iterateTrail(zoom) {
        this.clusterObjects.forEach(clusterObject => {
            let center = clusterObject.cluster.getCenter();
            let last = clusterObject.trailPositions[clusterObject.trailPositions.length - 1];
            if (!last || new THREE.Vector3(center.x, center.y, 0).distanceTo(last) > 0.1) {
                let positions = clusterObject.trailPositions;
                if (positions.length > this.props.trailSettings.length) {
                    let rem = clusterObject.trailPositions.shift();
                }
                positions.push(new THREE.Vector3(center.x, center.y, 0));
            }
        });
    }
    updatePositions(zoom) {
        if (!this.scene || !this.lineMesh) {
            return;
        }
        this.lineMesh.geometry.dispose();
        let lineGeometry = new THREE.Geometry();
        lineGeometry.vertices = [];
        lineGeometry.faces = [];
        lineGeometry.colors = [];
        let index = 0;
        this.clusterObjects.forEach(clusterObject => {
            let cluster = clusterObject.cluster;
            let center = cluster.getCenter();
            let mesh = clusterObject.mesh;
            mesh.position.set(center.x * zoom, center.y * zoom, -0.5);
            mesh.material.color = this.getColorForClusterObject(clusterObject);
            mesh.scale.set(this.props.globalPointSize[0], this.props.globalPointSize[0], this.props.globalPointSize[0]);
            clusterObject.children.forEach(child => {
                if (child.visible && this.props.displayMode == DisplayModeDuck_1.DisplayMode.StatesAndClusters) {
                    let sample = child.sample;
                    let dir = new THREE.Vector2(child.sample.x - center.x, child.sample.y - center.y).normalize();
                    let rigth = new three_1.Vector2(dir.y, -dir.x).multiplyScalar(this.devicePixelRatio);
                    let left = new three_1.Vector2(-dir.y, dir.x).multiplyScalar(this.devicePixelRatio);
                    lineGeometry.vertices.push(new THREE.Vector3(center.x * zoom + left.x, center.y * zoom + left.y, 0));
                    lineGeometry.vertices.push(new THREE.Vector3(center.x * zoom + rigth.x, center.y * zoom + rigth.y, 0));
                    lineGeometry.vertices.push(new THREE.Vector3(sample.x * zoom + left.x, sample.y * zoom + left.y, 0));
                    lineGeometry.vertices.push(new THREE.Vector3(sample.x * zoom + rigth.x, sample.y * zoom + rigth.y, 0));
                    let i = index * 4;
                    lineGeometry.faces.push(new THREE.Face3(i, i + 1, i + 2, new three_1.Vector3(0, 0, -1), new THREE.Color(clusterObject.lineColor.hex)));
                    lineGeometry.faces.push(new THREE.Face3(i + 1, i + 3, i + 2, new three_1.Vector3(0, 0, -1), new THREE.Color(clusterObject.lineColor.hex)));
                    index = index + 1;
                }
            });
        });
        this.lineMesh.geometry = lineGeometry;
    }
    getColorForClusterObject(clusterObject) {
        var _a, _b;
        if (this.props.selectedClusters.includes(clusterObject.cluster) || clusterObject.sampleConnection) {
            return new THREE.Color(SELECTED_COLOR);
        }
        if ((_b = (_a = this.props.stories.active) === null || _a === void 0 ? void 0 : _a.clusters) === null || _b === void 0 ? void 0 : _b.includes(clusterObject.cluster)) {
            return new THREE.Color(DEFAULT_COLOR);
        }
        else {
            return new THREE.Color(GRAYED);
        }
    }
    /**
     * Creates the visualization.
     */
    create() {
        this.clusterObjects = [];
        let lineMateral = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, vertexColors: true });
        lineMateral.opacity = 0.5;
        lineMateral.transparent = true;
        let lineGeometry = new THREE.Geometry();
        this.lineMesh = new THREE.Mesh(lineGeometry, lineMateral);
        this.lineMesh.position.set(0, 0, -1);
        this.scene.add(this.lineMesh);
        let scale = NamedCategoricalScales_1.NamedCategoricalScales.DARK2();
        this.props.clusters.forEach((cluster, ci) => {
            // Add circle to scene
            var geometry = new THREE.PlaneGeometry(this.devicePixelRatio * 16, this.devicePixelRatio * 16);
            var material = new THREE.MeshBasicMaterial({ color: new THREE.Color() });
            var circle = new THREE.Mesh(geometry, material);
            material.opacity = 0.92;
            material.transparent = true;
            var center = cluster.getCenter();
            circle.position.set(center.x, center.y, 0);
            this.clusterScene.add(circle);
            var clusterObject = {
                cluster: cluster,
                geometry: geometry,
                material: material,
                mesh: circle,
                children: [],
                trailPositions: [],
                lineColor: scale.map(ci),
                triangulatedMesh: {},
                sampleConnection: false
            };
            material.color = this.getColorForClusterObject(clusterObject);
            this.clusterObjects.push(clusterObject);
            if (this.props.clusterMode == ClusterModeDuck_1.ClusterMode.Multivariate) {
                // Create line geometry
                cluster.vectors.forEach(vector => {
                    clusterObject.children.push({
                        sample: vector,
                        visible: false
                    });
                });
            }
        });
    }
    // Activates the lines from given samples to their corresponding clusters
    highlightSamples(samples) {
        // Deactivate all lines
        this.deactivateAll();
        this.clusterObjects.forEach(clusterObject => {
            clusterObject.material.color = new THREE.Color(DEFAULT_COLOR);
        });
        samples.forEach(sample => {
            sample.clusterLabel.forEach(label => {
                let clusterObject = this.clusterObjects.find(e => e.cluster.label == label);
                if (clusterObject) {
                    clusterObject.sampleConnection = true;
                    clusterObject.children.forEach(child => {
                        if (child.sample == sample) {
                            child.visible = true;
                        }
                    });
                }
            });
        });
    }
    deactivateAll() {
        this.clusterObjects.forEach(clusterObject => {
            clusterObject.sampleConnection = false;
            clusterObject.material.color = this.getColorForClusterObject(clusterObject);
            clusterObject.children.forEach(child => {
                child.visible = false;
            });
        });
        if (this.clusterVis) {
            this.clusterVis.lineMeshes.forEach(mesh => {
                mesh.visible = false;
            });
            this.clusterVis.clusterMeshes.forEach(mesh => {
                mesh.visible = false;
            });
        }
    }
    highlightCluster(clusters) {
        this.deactivateAll();
        this.clusterObjects.forEach((clusterObject, index) => {
            var visible = clusters === null || clusters === void 0 ? void 0 : clusters.includes(clusterObject.cluster);
            clusterObject.material.color = visible ? new THREE.Color(SELECTED_COLOR) : new THREE.Color(DEFAULT_COLOR);
            clusterObject.children.forEach(child => {
                child.visible = visible;
            });
            if (this.clusterVis) {
                let triangulatedMesh = this.clusterVis.clusterMeshes[index];
                let triangulatedHull = this.clusterVis.lineMeshes[index];
                triangulatedHull.visible = visible;
                triangulatedMesh.visible = visible;
            }
        });
    }
    /**
     * Destroys the visualization.
     */
    destroy() {
        if (this.clusterObjects && this.clusterObjects.length > 0) {
            this.clusterObjects.forEach(clusterObject => {
                this.clusterScene.remove(clusterObject.mesh);
                clusterObject.geometry.dispose();
                clusterObject.material.dispose();
            });
        }
        if (this.lineMesh) {
            this.scene.remove(this.lineMesh);
            let lineMaterial = this.lineMesh.material;
            this.lineMesh.geometry.dispose();
            lineMaterial.dispose();
        }
    }
    /**
     * Creates the triangulated mesh that visualizes the clustering.
     * @param clusters an array of clusters
     */
    createTriangulatedMesh() {
        this.disposeTriangulatedMesh();
        if (this.props.dataset.multivariateLabels) {
            return;
        }
        var clusterMeshes = [];
        var lineMeshes = [];
        this.triangulationWorker.postMessage({
            messageType: 'triangulate',
            input: this.props.clusters.map(cluster => cluster.vectors.map(sample => [sample.x, sample.y]))
        });
        this.triangulationWorker.onmessage = (e) => {
            let Y = e.data;
            for (let idx = 0; idx < Y.length; idx++) {
                let test = Y[idx].triangulation;
                let polygon = Y[idx].hull;
                var points = [];
                let material = new THREE.LineBasicMaterial({ color: 0x909090 });
                polygon.forEach(pt => {
                    points.push(new THREE.Vector3(pt[0], pt[1], -5));
                });
                var linege = new THREE.BufferGeometry().setFromPoints(points);
                var line = new THREE.Line(linege, material);
                this.scalingScene.add(line);
                var geometry = new THREE.Geometry();
                var vi = 0;
                for (let i = 0; i < test.length; i += 6) {
                    var faceIn = [];
                    for (var x = 0; x < 3; x++) {
                        faceIn.push(vi);
                        var vertex = new THREE.Vector3(test[i + x * 2], test[i + x * 2 + 1]);
                        vi = vi + 1;
                        geometry.vertices.push(vertex);
                    }
                    geometry.faces.push(new THREE.Face3(faceIn[0], faceIn[1], faceIn[2]));
                }
                let meshMat = new THREE.MeshBasicMaterial({
                    color: 0x909090,
                    side: THREE.DoubleSide,
                    transparent: true,
                    opacity: 0.2
                });
                var mesh = new THREE.Mesh(geometry, meshMat);
                clusterMeshes.push(mesh);
                lineMeshes.push(line);
                this.scalingScene.add(mesh);
                mesh.visible = false;
                line.visible = false;
            }
        };
        this.clusterVis = { clusterMeshes: clusterMeshes, lineMeshes: lineMeshes };
    }
    /**
     * Destroys the triangulated view of the clusters.
     */
    disposeTriangulatedMesh() {
        if (this.clusterVis != null) {
            const { clusterMeshes, lineMeshes } = this.clusterVis;
            clusterMeshes.forEach(mesh => {
                mesh.geometry.dispose();
                mesh.material.dispose();
                this.scalingScene.remove(mesh);
            });
            lineMeshes.forEach(mesh => {
                mesh.geometry.dispose();
                mesh.material.dispose();
                this.scalingScene.remove(mesh);
            });
        }
    }
    /**
     * Render an empty div, so componentDidMount will get called.
     */
    render() {
        return React.createElement("div", null, this.props.stories.trace && this.props.stories.trace.mainPath.map((cluster, index) => {
            let screen = ViewTransform_1.ViewTransform.worldToScreen(cluster.getCenter(), this.props.viewTransform);
            return React.createElement(core_1.Typography, { style: { position: 'absolute', left: screen.x - 6, top: screen.y + 8, background: 'white' } }, index);
        }));
    }
});
